import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Plus, Pencil, Trash2 } from "lucide-react";

const blank = { name: "", logo_path: "", url: "", tier: "partner", order: 0 };

export default function AdminSponsorsPage() {
  const { current } = useChampionship() || {};
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    if (!current?.id) return;
    const { data } = await api.get(`/championships/${current.id}/sponsors`);
    setItems(data || []);
  }, [current?.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) await api.patch(`/sponsors/${editing.id}`, form);
      else await api.post(`/championships/${current.id}/sponsors`, form);
      toast.success("Saved");
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!confirm("Remove sponsor?")) return;
    try { await api.delete(`/sponsors/${id}`); toast.success("Removed"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, logo_path: s.logo_path || "", url: s.url || "", tier: s.tier || "partner", order: s.order || 0 }); setOpen(true); };

  return (
    <div className="p-8 lg:p-10" data-testid="admin-sponsors">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Sponsors</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2">Sponsors & partners</h1>
        </div>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sponsor-new-button">
          <Plus className="h-4 w-4 mr-2" /> New sponsor
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="border border-border bg-white p-4 flex items-center gap-4" data-testid={`sponsor-card-${s.id}`}>
            <div className="h-14 w-14 bg-secondary border border-border overflow-hidden flex items-center justify-center flex-shrink-0">
              {s.logo_path ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.logo_path}`} alt="" className="object-contain h-full w-full" /> : <span className="text-xs">No logo</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{s.name}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">{s.tier}</div>
            </div>
            <button onClick={() => openEdit(s)} className="text-primary"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(s.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-muted-foreground">No sponsors yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit sponsor" : "New sponsor"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sponsor-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Main</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                  <SelectItem value="supporter">Supporter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ImageUpload label="Logo" value={form.logo_path} onChange={(v) => setForm({ ...form, logo_path: v })} />
            <div className="space-y-2">
              <Label>Order</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sponsor-save-button">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
